import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	Label,
	ResponsiveTable,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Skeleton,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Textarea,
} from "@ancatag/at-design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	CheckCircle,
	Eye,
	MoreVertical,
	Shield,
	UserX,
	XCircle,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { usersService } from "../../services/admin";
import type { User, UserFilters } from "../../types/admin";

export default function AdminUsers() {
	const [page, setPage] = useState(1);
	const [limit] = useState(50);
	const [filters, setFilters] = useState<UserFilters>({});
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [userDetailOpen, setUserDetailOpen] = useState(false);
	const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
	const [suspendReason, setSuspendReason] = useState("");
	const [approveDialogOpen, setApproveDialogOpen] = useState(false);
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
	const [rejectReason, setRejectReason] = useState("");
	const [verifyIdDialogOpen, setVerifyIdDialogOpen] = useState(false);
	const [verifyIdApproved, setVerifyIdApproved] = useState(true);
	const [verifyIdRejectionReason, setVerifyIdRejectionReason] = useState("");
	const [activeTab, setActiveTab] = useState("all");

	const queryClient = useQueryClient();

	// Fetch users based on active tab
	const { data: usersData, isLoading } = useQuery({
		queryKey: ["admin-users", page, limit, filters, activeTab],
		queryFn: () => {
			if (activeTab === "pending-approval") {
				return usersService.getPendingApprovalUsers().then((users) => ({
					data: users,
					meta: {
						page: 1,
						limit: users.length,
						total: users.length,
						totalPages: 1,
						hasNext: false,
						hasPrev: false,
					},
				}));
			}
			if (activeTab === "pending-id") {
				return usersService.getPendingIdVerificationUsers().then((users) => ({
					data: users.map((u) => ({
						...u,
						role: "user" as const,
						status: "active" as const,
						emailVerified: false,
						planId: "",
						projectsCount: 0,
						apiKeysCount: 0,
						activeSessionsCount: 0,
						createdAt: u.createdAt,
					})),
					meta: {
						page: 1,
						limit: users.length,
						total: users.length,
						totalPages: 1,
						hasNext: false,
						hasPrev: false,
					},
				}));
			}
			return usersService.getUsers(page, limit, filters);
		},
	});

	// Fetch full user details when selected
	const { data: userDetails } = useQuery({
		queryKey: ["admin-user-details", selectedUser?.id],
		queryFn: () => {
			if (!selectedUser?.id) throw new Error("User ID is required");
			return usersService.getUser(selectedUser.id);
		},
		enabled: !!selectedUser && userDetailOpen,
	});

	// Mutations
	const suspendMutation = useMutation({
		mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
			usersService.suspendUser(userId, reason),
		onSuccess: () => {
			toast.success("User suspended successfully");
			queryClient.invalidateQueries({ queryKey: ["admin-users"] });
			setSuspendDialogOpen(false);
			setSuspendReason("");
		},
		onError: (error: any) => {
			toast.error(error.message || "Failed to suspend user");
		},
	});

	const approveMutation = useMutation({
		mutationFn: (userId: string) => usersService.approveUser(userId),
		onSuccess: () => {
			toast.success("User approved successfully");
			queryClient.invalidateQueries({ queryKey: ["admin-users"] });
			setApproveDialogOpen(false);
		},
		onError: (error: any) => {
			toast.error(error.message || "Failed to approve user");
		},
	});

	const rejectMutation = useMutation({
		mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
			usersService.rejectUser(userId, reason),
		onSuccess: () => {
			toast.success("User rejected successfully");
			queryClient.invalidateQueries({ queryKey: ["admin-users"] });
			setRejectDialogOpen(false);
			setRejectReason("");
		},
		onError: (error: any) => {
			toast.error(error.message || "Failed to reject user");
		},
	});

	const verifyIdMutation = useMutation({
		mutationFn: ({
			userId,
			approved,
			rejectionReason,
		}: {
			userId: string;
			approved: boolean;
			rejectionReason?: string;
		}) => usersService.verifyUserId(userId, approved, rejectionReason),
		onSuccess: () => {
			toast.success(
				`ID verification ${verifyIdApproved ? "approved" : "rejected"} successfully`,
			);
			queryClient.invalidateQueries({ queryKey: ["admin-users"] });
			setVerifyIdDialogOpen(false);
			setVerifyIdApproved(true);
			setVerifyIdRejectionReason("");
		},
		onError: (error: any) => {
			toast.error(error.message || "Failed to verify ID");
		},
	});

	const users = usersData?.data || [];
	const meta = usersData?.meta;

	const handleUserClick = (user: User) => {
		setSelectedUser(user);
		setUserDetailOpen(true);
	};

	const handleSuspend = () => {
		if (selectedUser) {
			suspendMutation.mutate({
				userId: selectedUser.id,
				reason: suspendReason || undefined,
			});
		}
	};

	const handleApprove = () => {
		if (selectedUser) {
			approveMutation.mutate(selectedUser.id);
		}
	};

	const handleReject = () => {
		if (selectedUser && rejectReason.trim()) {
			rejectMutation.mutate({
				userId: selectedUser.id,
				reason: rejectReason,
			});
		}
	};

	const handleVerifyId = () => {
		if (selectedUser) {
			verifyIdMutation.mutate({
				userId: selectedUser.id,
				approved: verifyIdApproved,
				rejectionReason: verifyIdApproved
					? undefined
					: verifyIdRejectionReason || undefined,
			});
		}
	};

	const getStatusBadge = (status: string) => {
		const variants: Record<string, string> = {
			active: "bg-green-500/20 text-green-600 border-green-500/30",
			suspended: "bg-red-500/20 text-red-600 border-red-500/30",
			deleted: "bg-gray-500/20 text-gray-600 border-gray-500/30",
		};
		return (
			variants[status] || "bg-gray-500/20 text-gray-600 border-gray-500/30"
		);
	};

	const getIdVerificationBadge = (status: string) => {
		const variants: Record<string, string> = {
			approved: "bg-green-500/20 text-green-600 border-green-500/30",
			pending: "bg-yellow-500/20 text-yellow-600 border-yellow-500/30",
			rejected: "bg-red-500/20 text-red-600 border-red-500/30",
		};
		return (
			variants[status] || "bg-gray-500/20 text-gray-600 border-gray-500/30"
		);
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						User Management
					</h1>
					<p className="text-muted-foreground mt-2">
						Manage users, approve accounts, and verify IDs
					</p>
				</div>
			</div>

			{/* Tabs */}
			<Tabs value={activeTab} onValueChange={setActiveTab}>
				<TabsList>
					<TabsTrigger value="all">All Users</TabsTrigger>
					<TabsTrigger value="pending-approval">
						Pending Approval
						{activeTab !== "pending-approval" && (
							<Badge variant="secondary" className="ml-2">
								{users.length}
							</Badge>
						)}
					</TabsTrigger>
					<TabsTrigger value="pending-id">
						Pending ID Verification
						{activeTab !== "pending-id" && (
							<Badge variant="secondary" className="ml-2">
								{users.length}
							</Badge>
						)}
					</TabsTrigger>
				</TabsList>

				{/* Filters - Only show for "all" tab */}
				{activeTab === "all" && (
					<Card className="mt-4">
						<CardHeader>
							<CardTitle className="text-lg">Filters</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
								<div className="space-y-2">
									<Label className="text-xs text-muted-foreground">
										Status
									</Label>
									<Select
										value={filters.status || "all"}
										onValueChange={(value) => {
											setFilters({
												...filters,
												status: value === "all" ? undefined : (value as any),
											});
											setPage(1);
										}}
									>
										<SelectTrigger>
											<SelectValue placeholder="All Statuses" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="all">All Statuses</SelectItem>
											<SelectItem value="active">Active</SelectItem>
											<SelectItem value="suspended">Suspended</SelectItem>
											<SelectItem value="deleted">Deleted</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label className="text-xs text-muted-foreground">Role</Label>
									<Select
										value={filters.role || "all"}
										onValueChange={(value) => {
											setFilters({
												...filters,
												role: value === "all" ? undefined : (value as any),
											});
											setPage(1);
										}}
									>
										<SelectTrigger>
											<SelectValue placeholder="All Roles" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="all">All Roles</SelectItem>
											<SelectItem value="user">User</SelectItem>
											<SelectItem value="admin">Admin</SelectItem>
											<SelectItem value="superAdmin">Super Admin</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label className="text-xs text-muted-foreground">
										ID Verification
									</Label>
									<Select
										value={filters.idVerificationStatus || "all"}
										onValueChange={(value) => {
											setFilters({
												...filters,
												idVerificationStatus:
													value === "all" ? undefined : (value as any),
											});
											setPage(1);
										}}
									>
										<SelectTrigger>
											<SelectValue placeholder="All" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="all">All</SelectItem>
											<SelectItem value="pending">Pending</SelectItem>
											<SelectItem value="approved">Approved</SelectItem>
											<SelectItem value="rejected">Rejected</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label className="text-xs text-muted-foreground">
										Search
									</Label>
									<Input
										placeholder="Search by email or name..."
										value={filters.search || ""}
										onChange={(e) => {
											setFilters({ ...filters, search: e.target.value });
											setPage(1);
										}}
									/>
								</div>
							</div>
						</CardContent>
					</Card>
				)}

				{/* Users Table */}
				<TabsContent value={activeTab} className="mt-4">
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">
								Users {meta && `(${meta.total.toLocaleString()} total)`}
							</CardTitle>
						</CardHeader>
						<CardContent>
							{isLoading ? (
								<UsersTableSkeleton />
							) : (
								<>
									<ResponsiveTable
										data={users}
										keyExtractor={(user) => user.id}
										columns={[
											{
												key: "email",
												header: "Email",
												mobileLabel: "Email",
												render: (user) => (
													<span className="font-mono text-sm">
														{user.email}
													</span>
												),
											},
											{
												key: "name",
												header: "Name",
												mobileLabel: "Name",
												render: (user) => (
													<span className="text-sm">{user.name || "—"}</span>
												),
											},
											{
												key: "role",
												header: "Role",
												mobileLabel: "Role",
												render: (user) => (
													<Badge variant="outline" className="text-xs">
														{user.role}
													</Badge>
												),
											},
											{
												key: "status",
												header: "Status",
												mobileLabel: "Status",
												render: (user) => (
													<Badge
														variant="outline"
														className={getStatusBadge(user.status)}
													>
														{user.status}
													</Badge>
												),
											},
											{
												key: "idVerification",
												header: "ID Verification",
												mobileLabel: "ID Verification",
												render: (user) => (
													<Badge
														variant="outline"
														className={getIdVerificationBadge(
															user.idVerificationStatus,
														)}
													>
														{user.idVerificationStatus}
													</Badge>
												),
											},
											{
												key: "plan",
												header: "Plan",
												mobileLabel: "Plan",
												render: (user) => (
													<span className="text-sm">
														{user.plan?.displayName || user.plan?.name || "—"}
													</span>
												),
											},
											{
												key: "projects",
												header: "Projects",
												mobileLabel: "Projects",
												hideOnMobile: true,
												render: (user) => (
													<span className="text-sm">{user.projectsCount}</span>
												),
											},
											{
												key: "apiKeys",
												header: "API Keys",
												mobileLabel: "API Keys",
												hideOnMobile: true,
												render: (user) => (
													<span className="text-sm">{user.apiKeysCount}</span>
												),
											},
											{
												key: "actions",
												header: "Actions",
												mobileLabel: "Actions",
												render: (user) => (
													<DropdownMenu>
														<DropdownMenuTrigger asChild>
															<Button variant="ghost" size="icon">
																<MoreVertical className="h-4 w-4" />
															</Button>
														</DropdownMenuTrigger>
														<DropdownMenuContent align="end">
															<DropdownMenuItem
																onClick={() => handleUserClick(user)}
															>
																<Eye className="h-4 w-4 mr-2" />
																View Details
															</DropdownMenuItem>
															{user.idVerificationStatus === "pending" &&
																user.idVerificationFileUrl && (
																	<DropdownMenuItem
																		onClick={() => {
																			setSelectedUser(user);
																			setVerifyIdDialogOpen(true);
																		}}
																	>
																		<Shield className="h-4 w-4 mr-2" />
																		Verify ID
																	</DropdownMenuItem>
																)}
															{user.status !== "suspended" && (
																<DropdownMenuItem
																	onClick={() => {
																		setSelectedUser(user);
																		setSuspendDialogOpen(true);
																	}}
																>
																	<UserX className="h-4 w-4 mr-2" />
																	Suspend
																</DropdownMenuItem>
															)}
														</DropdownMenuContent>
													</DropdownMenu>
												),
											},
										]}
										onRowClick={handleUserClick}
										emptyMessage="No users found matching your filters."
									/>

									{/* Pagination */}
									{meta && meta.totalPages > 1 && (
										<div className="flex items-center justify-between mt-4">
											<div className="text-sm text-muted-foreground">
												Page {meta.page} of {meta.totalPages}
											</div>
											<div className="flex items-center gap-2">
												<Button
													variant="outline"
													size="sm"
													onClick={() => setPage((p) => Math.max(1, p - 1))}
													disabled={!meta.hasPrev}
												>
													Previous
												</Button>
												<Button
													variant="outline"
													size="sm"
													onClick={() =>
														setPage((p) => Math.min(meta.totalPages, p + 1))
													}
													disabled={!meta.hasNext}
												>
													Next
												</Button>
											</div>
										</div>
									)}
								</>
							)}
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>

			{/* User Detail Dialog */}
			<Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
				<DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>User Details</DialogTitle>
						<DialogDescription>
							{selectedUser?.email} - Full user information and management
						</DialogDescription>
					</DialogHeader>
					{userDetails && (
						<div className="space-y-6 mt-4">
							{/* User Info */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<Label className="text-xs text-muted-foreground">Email</Label>
									<p className="text-sm font-mono">{userDetails.email}</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">Name</Label>
									<p className="text-sm">{userDetails.name || "—"}</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">Role</Label>
									<Badge variant="outline">{userDetails.role}</Badge>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Status
									</Label>
									<Badge
										variant="outline"
										className={getStatusBadge(userDetails.status)}
									>
										{userDetails.status}
									</Badge>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">Plan</Label>
									<p className="text-sm">
										{userDetails.plan?.displayName ||
											userDetails.plan?.name ||
											"—"}
									</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										ID Verification
									</Label>
									<Badge
										variant="outline"
										className={getIdVerificationBadge(
											userDetails.idVerificationStatus,
										)}
									>
										{userDetails.idVerificationStatus}
									</Badge>
								</div>
							</div>

							{/* ID Document Viewer */}
							{userDetails.idVerificationFileUrl && (
								<div>
									<Label className="text-xs text-muted-foreground mb-2 block">
										ID Document
									</Label>
									<div className="border rounded-lg p-4 bg-muted/50">
										{userDetails.idVerificationFileUrl.match(
											/\.(jpg|jpeg|png|gif|webp)$/i,
										) ? (
											<img
												src={`${import.meta.env.VITE_API_BASE_URL || "http://localhost:3001"}${userDetails.idVerificationFileUrl}`}
												alt="ID Document"
												className="max-w-full h-auto rounded-md"
											/>
										) : (
											<div className="text-center py-8">
												<p className="text-sm text-muted-foreground mb-2">
													ID Document (PDF or Image)
												</p>
												<a
													href={`${import.meta.env.VITE_API_BASE_URL || "http://localhost:3001"}${userDetails.idVerificationFileUrl}`}
													target="_blank"
													rel="noopener noreferrer"
													className="text-primary hover:underline"
												>
													View Document
												</a>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Projects */}
							{userDetails.projects && userDetails.projects.length > 0 && (
								<div>
									<Label className="text-xs text-muted-foreground mb-2 block">
										Projects ({userDetails.projects.length})
									</Label>
									<div className="space-y-2">
										{userDetails.projects.map((project) => (
											<Card key={project.id}>
												<CardContent className="p-3">
													<div className="flex items-center justify-between">
														<div>
															<p className="text-sm font-medium">
																{project.name}
															</p>
															<p className="text-xs text-muted-foreground">
																{project._count.apiKeys} API keys,{" "}
																{project._count.routeConfigAccess} route configs
															</p>
														</div>
													</div>
												</CardContent>
											</Card>
										))}
									</div>
								</div>
							)}

							{/* API Keys */}
							{userDetails.apiKeys && userDetails.apiKeys.length > 0 && (
								<div>
									<Label className="text-xs text-muted-foreground mb-2 block">
										API Keys ({userDetails.apiKeys.length})
									</Label>
									<div className="space-y-2">
										{userDetails.apiKeys.map((key) => (
											<Card key={key.id}>
												<CardContent className="p-3">
													<div className="flex items-center justify-between">
														<div>
															<p className="text-sm font-medium">{key.name}</p>
															<p className="text-xs text-muted-foreground font-mono">
																{key.keyPrefix}...
															</p>
														</div>
														<Badge
															variant={key.isActive ? "default" : "secondary"}
														>
															{key.isActive ? "Active" : "Inactive"}
														</Badge>
													</div>
												</CardContent>
											</Card>
										))}
									</div>
								</div>
							)}

							{/* Actions */}
							<div className="flex flex-wrap gap-2 pt-4 border-t">
								{userDetails.idVerificationStatus === "pending" &&
									userDetails.idVerificationFileUrl && (
										<Button
											variant="outline"
											onClick={() => {
												setVerifyIdDialogOpen(true);
											}}
										>
											<Shield className="h-4 w-4 mr-2" />
											Verify ID
										</Button>
									)}
								<Button
									variant="outline"
									onClick={() => {
										setApproveDialogOpen(true);
									}}
								>
									<CheckCircle className="h-4 w-4 mr-2" />
									Approve User
								</Button>
								<Button
									variant="outline"
									onClick={() => {
										setRejectDialogOpen(true);
									}}
								>
									<XCircle className="h-4 w-4 mr-2" />
									Reject User
								</Button>
								{userDetails.status !== "suspended" && (
									<Button
										variant="destructive"
										onClick={() => {
											setSuspendDialogOpen(true);
										}}
									>
										<UserX className="h-4 w-4 mr-2" />
										Suspend User
									</Button>
								)}
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>

			{/* Suspend Dialog */}
			<Dialog open={suspendDialogOpen} onOpenChange={setSuspendDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Suspend User</DialogTitle>
						<DialogDescription>
							Suspend user account. They will not be able to access the
							platform.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 mt-4">
						<div className="space-y-2">
							<Label htmlFor="suspend-reason">Reason (optional)</Label>
							<Textarea
								id="suspend-reason"
								placeholder="Enter reason for suspension..."
								value={suspendReason}
								onChange={(e) => setSuspendReason(e.target.value)}
							/>
						</div>
						<div className="flex justify-end gap-2">
							<Button
								variant="outline"
								onClick={() => {
									setSuspendDialogOpen(false);
									setSuspendReason("");
								}}
							>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={handleSuspend}
								disabled={suspendMutation.isPending}
							>
								{suspendMutation.isPending ? "Suspending..." : "Suspend User"}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Approve Dialog */}
			<Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Approve User</DialogTitle>
						<DialogDescription>
							Approve this user account. They will gain full access to the
							platform.
						</DialogDescription>
					</DialogHeader>
					<div className="flex justify-end gap-2 mt-4">
						<Button
							variant="outline"
							onClick={() => setApproveDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button
							onClick={handleApprove}
							disabled={approveMutation.isPending}
						>
							{approveMutation.isPending ? "Approving..." : "Approve User"}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Reject Dialog */}
			<Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Reject User</DialogTitle>
						<DialogDescription>
							Reject this user account. They will not be able to access the
							platform.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 mt-4">
						<div className="space-y-2">
							<Label htmlFor="reject-reason">Reason *</Label>
							<Textarea
								id="reject-reason"
								placeholder="Enter reason for rejection..."
								value={rejectReason}
								onChange={(e) => setRejectReason(e.target.value)}
								required
							/>
						</div>
						<div className="flex justify-end gap-2">
							<Button
								variant="outline"
								onClick={() => {
									setRejectDialogOpen(false);
									setRejectReason("");
								}}
							>
								Cancel
							</Button>
							<Button
								variant="destructive"
								onClick={handleReject}
								disabled={rejectMutation.isPending || !rejectReason.trim()}
							>
								{rejectMutation.isPending ? "Rejecting..." : "Reject User"}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Verify ID Dialog */}
			<Dialog open={verifyIdDialogOpen} onOpenChange={setVerifyIdDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Verify ID</DialogTitle>
						<DialogDescription>
							Approve or reject the user's ID verification.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 mt-4">
						<div className="space-y-2">
							<Label>Decision</Label>
							<Select
								value={verifyIdApproved ? "approve" : "reject"}
								onValueChange={(value) =>
									setVerifyIdApproved(value === "approve")
								}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="approve">Approve</SelectItem>
									<SelectItem value="reject">Reject</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{!verifyIdApproved && (
							<div className="space-y-2">
								<Label htmlFor="verify-id-reason">Rejection Reason</Label>
								<Textarea
									id="verify-id-reason"
									placeholder="Enter reason for rejection..."
									value={verifyIdRejectionReason}
									onChange={(e) => setVerifyIdRejectionReason(e.target.value)}
								/>
							</div>
						)}
						<div className="flex justify-end gap-2">
							<Button
								variant="outline"
								onClick={() => {
									setVerifyIdDialogOpen(false);
									setVerifyIdApproved(true);
									setVerifyIdRejectionReason("");
								}}
							>
								Cancel
							</Button>
							<Button
								onClick={handleVerifyId}
								disabled={verifyIdMutation.isPending}
							>
								{verifyIdMutation.isPending
									? "Processing..."
									: verifyIdApproved
										? "Approve ID"
										: "Reject ID"}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}

function UsersTableSkeleton() {
	return (
		<div className="space-y-2">
			{[1, 2, 3, 4, 5].map((i) => (
				<Skeleton key={i} className="h-12 w-full" />
			))}
		</div>
	);
}
